import { useEffect, useRef } from 'react'
import { normalizeSelectText } from './select/text'

/**
 * Minimal option shape required for keyboard typeahead navigation.
 */
export type TypeaheadOption = {
  value: string
  label: string
  disabled?: boolean
}

/**
 * Inputs for `useSelectTypeahead`.
 */
export type UseSelectTypeaheadArgs = {
  options: TypeaheadOption[]
  value: string
  onChange: (nextValue: string) => void
}

/**
 * Returns a key handler helper that selects the next enabled option whose label
 * matches the accumulated typeahead query.
 */
export const useSelectTypeahead = ({ options, value, onChange }: UseSelectTypeaheadArgs) => {
  const typeaheadQueryRef = useRef('')
  const typeaheadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (typeaheadTimeoutRef.current) clearTimeout(typeaheadTimeoutRef.current)
    },
    [],
  )

  return (key: string) => {
    if (!key || key.length !== 1 || /\s/.test(key)) return

    const enabledOptions = options.filter((option) => !option.disabled)
    if (!enabledOptions.length) return

    const currentIndex = enabledOptions.findIndex((option) => option.value === value)
    const orderedOptions =
      currentIndex >= 0
        ? [...enabledOptions.slice(currentIndex + 1), ...enabledOptions.slice(0, currentIndex + 1)]
        : enabledOptions

    typeaheadQueryRef.current += normalizeSelectText(key)
    if (typeaheadTimeoutRef.current) clearTimeout(typeaheadTimeoutRef.current)
    typeaheadTimeoutRef.current = setTimeout(() => {
      typeaheadQueryRef.current = ''
    }, 600)

    const byQuery = orderedOptions.find((option) => normalizeSelectText(option.label).startsWith(typeaheadQueryRef.current))
    if (byQuery) {
      onChange(byQuery.value)
      return
    }

    if (typeaheadQueryRef.current.length <= 1) return
    const lastChar = typeaheadQueryRef.current.slice(-1)
    const byLastChar = orderedOptions.find((option) => normalizeSelectText(option.label).startsWith(lastChar))
    if (byLastChar) {
      typeaheadQueryRef.current = lastChar
      onChange(byLastChar.value)
    }
  }
}
